import { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import {
  useCreateMaterialMutation,
  // useDeleteMaterialMutation,
  useGetAllMaterialsQuery,
  useUpdateMaterialMutation,
  useLazyCheckMaterialUsageQuery,
  useDeactivateMaterialMutation,
  useDeleteMaterialWithReplacementMutation,
  useLazyGetAllMaterialsQuery,
} from 'services/material.service';
import { showAlertNotification } from 'components/common';
import { QueryParams, CheckUsedResponse, TableEnum } from 'types';
import { useCheckUniqueMutation, useGenerateCodeMutation } from 'services';
import { useUrlParams } from './useUrlParams';
import { buildFullParams } from 'utils/filterBuilder';
import { useEntitySearch } from './useEntitySearch';
import { MaterialRecord } from 'types/common';
import { Material } from 'models/Material';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';

export const useMaterialOperations = () => {
  // Form instance
  const [form] = Form.useForm();

  // States
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [materialToEdit, setMaterialToEdit] = useState<MaterialRecord | null>(null);
  const [materialToDelete, setMaterialToDelete] = useState<MaterialRecord | null>(null);
  const [materialToCopy, setMaterialToCopy] = useState<MaterialRecord | null>(null);
  const [originalCode, setOriginalCode] = useState<string>('');
  const [materials, setMaterials] = useState<MaterialRecord[]>([]);
  const pageKey = 'material-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<MaterialRecord> | undefined;
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    searchKey: 'name|code',
    searchValue: tableState?.search ?? '',
    filter: tableState?.filter ?? '',
  });
  const { addChangeParamToUrl, removeChangeParamFromUrl } = useUrlParams();

  // Replacement states
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);
  const [materialToDeactivate, setMaterialToDeactivate] = useState<MaterialRecord | null>(null);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [usageResult, setUsageResult] = useState<CheckUsedResponse | null>(null);

  // API calls
  const { data, isLoading, isError, isFetching } = useGetAllMaterialsQuery({
    query: filteredQuery as QueryParams,
  });
  const [createMaterial, { isLoading: isCreating }] = useCreateMaterialMutation();
  const [updateMaterial, { isLoading: isUpdating }] = useUpdateMaterialMutation();
  const [generateCode] = useGenerateCodeMutation();
  const [checkUnique] = useCheckUniqueMutation();

  // Replacement APIs
  const [checkMaterialUsage] = useLazyCheckMaterialUsageQuery();
  const [deactivateMaterial, { isLoading: isDeactivating }] = useDeactivateMaterialMutation();
  const [deleteMaterialWithReplacement, { isLoading: isDeletingReplacement }] =
    useDeleteMaterialWithReplacementMutation();
  const [searchMaterials, { isLoading: isSearchingReplacements }] = useLazyGetAllMaterialsQuery();

  // UseEntitySearch for replacement materials
  const {
    searchResults: replacementMaterials,
    handleSearch: handleReplacementSearch,
    resetSearch: resetReplacementSearch,
    isSearching: isSearchingReplacementsState,
  } = useEntitySearch({
    searchFunction: searchMaterials,
    formatResults: (response) => {
      if (!response?.data?.data?.rows) return [];
      return response.data?.data.rows
        .filter((material) => material.isActive)
        .map((material) => ({
          key: material.id,
          id: material.id,
          code: material.code,
          name: material.name,
          isActive: material.isActive,
          costPrice: material.costPrice,
          salePrice: material.salePrice,
          quantity: material.quantity || 0,
          description: material.description,
          createdAt: material.createdAt ? new Date(material.createdAt) : new Date(),
          updatedAt: material.updatedAt ? new Date(material.updatedAt) : new Date(),
        }));
    },
    filterResults: (material) => material.key !== materialToDeactivate?.key,
  });

  const getActiveMaterials = useCallback(() => {
    if (replacementMaterials.length > 0) {
      return replacementMaterials;
    }
    return materials
      .filter((material) => material.isActive)
      .filter((material) => String(material.key) !== String(materialToDeactivate?.key))
      .map((material) => ({
        ...material,
      }));
  }, [materials, replacementMaterials, materialToDeactivate]);

  // Map materials data when it changes
  useEffect(() => {
    if (data && !isError) {
      try {
        const materialsData = data.data.rows.map((item: Material) => ({
          key: item.id,
          id: item.id,
          code: item.code || 'PLACEHOLDER',
          name: item.name,
          isActive: item.isActive,
          costPrice: item.costPrice,
          salePrice: item.salePrice,
          quantity: item.quantity || 0,
          description: item.description,
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
        }));
        setMaterials(materialsData);
        dispatch(
          setTableParams({
            pageKey,
            params: {
              lastFetched: Date.now(),
              search: filteredQuery.searchValue ?? '',
              page: filteredQuery.page ?? 1,
              pageSize: filteredQuery.size ?? 10,
              filter: filteredQuery.filter ?? '',
            } as unknown as Partial<TableCacheItem<unknown>>,
          }),
        );
      } catch (error) {
        console.error('Error processing material data:', error);
      }
    }
  }, [data, isError]);

  // Handle deactivate with replacement
  const handleDeactivateWithReplacement = useCallback(
    async (replacementMaterialId: string) => {
      if (!materialToDeactivate) return;
      try {
        await deactivateMaterial({
          id: materialToDeactivate.id as string,
          payload: {
            replaceEntityId: replacementMaterialId,
            data: usageResult?.data || [],
          },
        }).unwrap();
        setUsageResult(null);
        addChangeParamToUrl();
        setIsDeactivateModalOpen(false);
        setMaterialToDeactivate(null);
        showAlertNotification({
          type: 'success',
          title: 'Thao tác thành công',
          message: `Bạn đã hủy kích hoạt nguyên liệu ${materialToDeactivate.name} thành công`,
        });
      } catch (error) {
        console.error('Error deactivating material:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: 'Không thể thay thế nguyên liệu. Vui lòng thử lại sau.',
        });
      } finally {
        setIsDeactivateModalOpen(false);
        setMaterialToDeactivate(null);
      }
    },
    [materialToDeactivate, deactivateMaterial, usageResult, addChangeParamToUrl],
  );

  // Handle delete with replacement
  const handleDeleteWithReplacement = useCallback(
    async (replacementMaterialId: string) => {
      if (!materialToDeactivate) return;
      try {
        await deleteMaterialWithReplacement({
          id: materialToDeactivate.id as string,
          payload: {
            replaceEntityId: replacementMaterialId,
            data: usageResult?.data || [],
          },
        }).unwrap();
        setUsageResult(null);
        removeChangeParamFromUrl();
        setIsDeactivateModalOpen(false);
        setMaterialToDelete(null);
        showAlertNotification({
          type: 'success',
          title: 'Thao tác thành công',
          message: `Bạn đã xóa nguyên liệu ${materialToDeactivate.name} thành công`,
        });
      } catch (error) {
        console.error('Error deleting material:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: 'Không thể xóa nguyên liệu. Vui lòng thử lại sau.',
        });
      } finally {
        setIsDeactivateModalOpen(false);
        setIsDeleteModalOpen(false);
        setMaterialToDelete(null);
      }
    },
    [materialToDeactivate, deleteMaterialWithReplacement, usageResult, removeChangeParamFromUrl],
  );

  // Handle status toggle (check usage)
  const handleStatusToggle = useCallback(
    async (checked: boolean, record: MaterialRecord) => {
      if (checked) {
        try {
          await updateMaterial({
            id: record.key as string,
            isActive: checked,
          });
          addChangeParamToUrl();
          showAlertNotification({
            type: 'success',
            title: 'Thao tác thành công',
            message: `Nguyên liệu ${record.name} đã được kích hoạt thành công`,
          });
        } catch (error) {
          console.error('Error updating material status:', error);
          showAlertNotification({
            type: 'error',
            title: 'Lỗi',
            message: 'Không thể cập nhật trạng thái nguyên liệu. Vui lòng thử lại sau.',
          });
        }
        return;
      }

      try {
        // Check usage before deactivating
        const usage = await checkMaterialUsage(record.key as string).unwrap();
        if (usage.data && usage.data.some((item) => item.ids.length > 0)) {
          setUsageResult(usage);
          setMaterialToDeactivate(record);
          setIsDeactivateModalOpen(true);
          setIsDeleteMode(false);
          return;
        }

        // If not in use, deactivate directly
        await updateMaterial({
          id: record.key as string,
          isActive: checked,
        });
        setUsageResult(null);
        addChangeParamToUrl();
        showAlertNotification({
          type: 'success',
          title: 'Thao tác thành công',
          message: `Nguyên liệu ${record.name} đã được hủy kích hoạt thành công`,
        });
      } catch (error) {
        console.error('Error updating material status:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: 'Không thể kiểm tra trạng thái nguyên liệu. Vui lòng thử lại sau.',
        });
      }
    },
    [updateMaterial, checkMaterialUsage, addChangeParamToUrl],
  );

  // Handle delete (check usage)
  const handleDelete = useCallback((record: MaterialRecord): void => {
    setMaterialToDelete(record);
    setIsDeleteModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!materialToDelete) return;
    try {
      // Check usage before deleting
      const usage = await checkMaterialUsage(materialToDelete.key as string).unwrap();
      if (usage.data && usage.data.some((item) => item.ids.length > 0)) {
        setUsageResult(usage);
        setMaterialToDeactivate(materialToDelete);
        setIsDeactivateModalOpen(true);
        setIsDeleteModalOpen(false);
        setIsDeleteMode(true);
        return;
      }

      // If not in use, delete directly
      await deleteMaterialWithReplacement({
        id: materialToDelete?.id as string,
        payload: {
          replaceEntityId: materialToDelete?.id as string,
          data: [],
        },
      }).unwrap();
      setUsageResult(null);
      removeChangeParamFromUrl();
      showAlertNotification({
        type: 'success',
        title: 'Thao tác thành công',
        message: `Nguyên liệu ${materialToDelete.name} đã được xóa thành công`,
      });
      setIsDeleteModalOpen(false);
      setMaterialToDelete(null);
    } catch (error) {
      console.error('Error deleting material:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Không thể xóa nguyên liệu. Vui lòng thử lại sau.',
      });
    }
  }, [materialToDelete, checkMaterialUsage, deleteMaterialWithReplacement, removeChangeParamFromUrl]);

  const cancelDeactivate = useCallback(() => {
    setIsDeactivateModalOpen(false);
    setMaterialToDeactivate(null);
    setIsDeleteMode(false);
    resetReplacementSearch();
  }, [resetReplacementSearch]);

  const handleEdit = useCallback(
    (record: MaterialRecord): void => {
      setMaterialToEdit(record);
      setOriginalCode(record.code);
      form.setFieldsValue({
        name: record.name,
        code: record.code,
        costPrice: record.costPrice,
        salePrice: record.salePrice,
        quantity: record.quantity,
        isActive: record.isActive,
        description: record.description as string,
      });
      setIsModalOpen(true);
    },
    [form],
  );

  const handleGenerate = useCallback(async () => {
    try {
      const prefix = 'NL';
      const generatedCode = await generateCode({ code: prefix }).unwrap();
      return generatedCode;
    } catch (error) {
      console.error('Error generating code:', error);
      const prefix = 'NL';
      const timestamp = Date.now().toString().slice(-6);
      const generatedCode = `${prefix}${timestamp}`;
      return generatedCode;
    }
  }, [generateCode]);

  const handleCopy = useCallback(
    (record: MaterialRecord): void => {
      setMaterialToCopy(record);
      form.setFieldsValue({
        name: record.name + ' (Bản sao)',
        costPrice: record.costPrice,
        salePrice: record.salePrice,
        quantity: record.quantity,
        isActive: record.isActive,
        description: record.description as string,
        code: '',
      });
      setMaterialToEdit(null);
      setIsModalOpen(true);
    },
    [form],
  );

  const cancelDelete = useCallback((): void => {
    setIsDeleteModalOpen(false);
    setMaterialToDelete(null);
  }, []);

  const handleAddNew = useCallback((): void => {
    setMaterialToEdit(null);
    setMaterialToCopy(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      costPrice: 0,
      salePrice: 0,
      quantity: 0,
    });
    setIsModalOpen(true);
  }, [form]);

  const handleCancel = useCallback((): void => {
    setIsModalOpen(false);
    setMaterialToEdit(null);
    setMaterialToCopy(null);
    setOriginalCode('');
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(
    async (values: Partial<Material>): Promise<void> => {
      try {
        const materialData = {
          code: values.code,
          name: values.name,
          costPrice: Number(values.costPrice) || 0,
          salePrice: Number(values.salePrice) || 0,
          quantity: Number(values.quantity) || 0,
          isActive: values.isActive,
          description: values.description || '',
        };

        if (materialToEdit) {
          await updateMaterial({
            id: materialToEdit.key as string,
            ...materialData,
          } as unknown as Material).unwrap();
          addChangeParamToUrl();
          showAlertNotification({
            type: 'success',
            title: 'Thao tác thành công',
            message: `Nguyên liệu ${values.name} đã được cập nhật thành công`,
          });
        } else {
          const response = await checkUnique({
            table: TableEnum.Materials,
            field: 'code',
            value: values.code || '',
          });

          if (response?.data?.data.exists) {
            form.setFields([
              {
                name: 'code',
                errors: [`Mã nguyên liệu "${values.code}" đã tồn tại. Vui lòng sử dụng mã khác.`],
              },
            ]);
            showAlertNotification({
              type: 'error',
              title: 'Lỗi',
              message: `Mã nguyên liệu "${values.code}" đã tồn tại. Vui lòng sử dụng mã khác.`,
            });
            return;
          }
          await createMaterial(materialData as unknown as Material).unwrap();
          addChangeParamToUrl();
          showAlertNotification({
            type: 'success',
            title: 'Thao tác thành công',
            message: `Nguyên liệu ${values.name} đã được thêm thành công`,
          });
        }

        setIsModalOpen(false);
        setOriginalCode('');
        form.resetFields();
      } catch (error) {
        console.error('Error submitting material:', error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: 'Không thể lưu nguyên liệu. Vui lòng thử lại sau.',
        });
      }
    },
    [materialToEdit, form, createMaterial, updateMaterial, addChangeParamToUrl, checkUnique],
  );

  const handlePagination = useCallback(
    (query: QueryParams) => {
      try {
        const columnArray = ['isActive'];
        const filterQuery = buildFullParams(query, undefined, columnArray);
        setFilteredQuery({
          ...filteredQuery,
          ...filterQuery,
        });
      } catch (error) {
        console.error('handlePagination error:', error);
      }
    },
    [filteredQuery],
  );

  return {
    // Data
    materials,
    isLoading,
    isFetching,
    data,

    // State
    isModalOpen,
    isDeleteModalOpen,
    materialToEdit,
    materialToDelete,
    materialToCopy,
    form,
    filteredQuery,
    isSubmitting: isCreating || isUpdating || isDeactivating || isDeletingReplacement || isSearchingReplacementsState,
    originalCode,
    isDeactivateModalOpen,
    materialToDeactivate,
    isDeleteMode,
    isDeactivating,
    isDeletingReplacement,
    isSearchingReplacements: isSearchingReplacements || isSearchingReplacementsState,
    replacementMaterials,
    usageResult,
    tableState,

    // Handlers
    handleStatusToggle,
    handleEdit,
    handleCopy,
    handleDelete,
    confirmDelete,
    cancelDelete,
    handleAddNew,
    handleCancel,
    handleGenerate,
    handleSubmit,
    handlePagination,
    handleDeactivateWithReplacement,
    handleDeleteWithReplacement,
    getActiveMaterials,
    cancelDeactivate,
    handleReplacementSearch,
    resetReplacementSearch,
    setFilteredQuery,
  };
};
